<template>
    <div class="frozen-page">
  
      <!-- ============================= -->
      <!--         🌌 BACKGROUND         -->
      <!-- ============================= -->
  
      <div class="frozen-glow frozen-glow--one"></div>
      <div class="frozen-glow frozen-glow--two"></div>
  
  
      <!-- ============================= -->
      <!--          🧊 CONTENT           -->
      <!-- ============================= -->
  
      <main class="frozen-content">
  
        <div class="frozen-card">
  
          <!-- ============================= -->
          <!--            ICON               -->
          <!-- ============================= -->
  
          <div class="freeze-icon">
            <div class="freeze-icon-inner">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 3V21M4.2 7.5L19.8 16.5M19.8 7.5L4.2 16.5"
                  stroke="currentColor"
                  stroke-width="1.65"
                  stroke-linecap="round"
                />
  
                <path
                  d="M12 3L9.8 5.2M12 3L14.2 5.2M12 21L9.8 18.8M12 21L14.2 18.8"
                  stroke="currentColor"
                  stroke-width="1.65"
                  stroke-linecap="round"
                />
              </svg>
            </div>
          </div>
  
  
          <!-- ============================= -->
          <!--            TEXT               -->
          <!-- ============================= -->
  
          <div class="frozen-copy">
  
            <span class="frozen-badge">
              Аккаунт заморожен
            </span>
  
            <h1>
              Ваш профиль сейчас на паузе
            </h1>
  
            <p>
              Все данные, резюме и настройки сохранены.
              Разморозьте аккаунт, чтобы снова пользоваться Hr³.
            </p>
  
          </div>
  
  
          <!-- ============================= -->
          <!--          INFORMATION          -->
          <!-- ============================= -->
  
          <div class="frozen-info">
  
            <div class="info-dot"></div>
  
            <span>
              При восстановлении аккаунта все ваши данные
              вернутся автоматически.
            </span>
  
          </div>
  
  
          <!-- ============================= -->
          <!--            ACTIONS            -->
          <!-- ============================= -->
  
          <div class="frozen-actions">
  
            <button
              class="unfreeze-button"
              type="button"
              :disabled="isLoading"
              @click="handleUnfreeze"
            >
              <span v-if="!isLoading">
                Разморозить аккаунт
              </span>
  
              <span
                v-else
                class="button-loading"
              >
                <span class="button-spinner"></span>
  
                Восстанавливаем...
              </span>
            </button>
  
  
            <button
              class="logout-button"
              type="button"
              :disabled="isLoading"
              @click="handleLogout"
            >
              Выйти из аккаунта
            </button>
  
          </div>
  
  
          <!-- ============================= -->
          <!--             ERROR             -->
          <!-- ============================= -->
  
          <transition name="error-fade">
            <div
              v-if="errorMessage"
              class="frozen-error"
            >
              {{ errorMessage }}
            </div>
          </transition>
  
        </div>
  
      </main>
  
  
      <!-- ============================= -->
      <!--            FOOTER             -->
      <!-- ============================= -->
  
      <MiniFooter class="frozen-footer" />
  
    </div>
  </template>
  
  
  <script setup>
  import { ref } from "vue";
  import { useRouter } from "vue-router";
  
  import {
    getAuth,
    signOut
  } from "firebase/auth";
  
  import UserService from "../api/UserService";
  import MiniFooter from "../components/MiniFooter.vue";
  
  
  const router = useRouter();
  
  const isLoading = ref(false);
  const errorMessage = ref("");
  
  
  // =============================
  //      ☀️ UNFREEZE ACCOUNT
  // =============================
  
  const handleUnfreeze = async () => {
    if (isLoading.value) return;
  
    errorMessage.value = "";
    isLoading.value = true;
  
    try {
  
      console.log(
        "☀️ Начинаем восстановление аккаунта..."
      );
  
      const result =
        await UserService.unfreezeAccount();
  
  
      if (!result?.success) {
        throw new Error(
          result?.error ||
          "Не удалось восстановить аккаунт"
        );
      }
  
  
      console.log(
        "✅ Аккаунт восстановлен:",
        result
      );
  
  
      // =============================
      //       🔄 LOAD PROFILE
      // =============================
  
      const profile =
        await UserService.getProfile();
  
  
      if (!profile) {
        throw new Error(
          "Аккаунт восстановлен, но профиль не удалось загрузить"
        );
      }
  
  
      console.log(
        "✅ Профиль после разморозки получен"
      );
  
  
      // =============================
      //           🚀 FEED
      // =============================
  
      await router.replace(
        "/feed"
      );
  
    } catch (error) {
  
      console.error(
        "❌ Ошибка восстановления аккаунта:",
        error
      );
  
      errorMessage.value =
        error?.message ||
        "Не удалось восстановить аккаунт. Попробуйте ещё раз.";
  
    } finally {
  
      isLoading.value = false;
    }
  };
  
  
  // =============================
  //           🚪 LOGOUT
  // =============================
  
  const handleLogout = async () => {
    if (isLoading.value) return;
  
    isLoading.value = true;
  
    try {
  
      const auth = getAuth();
  
      await signOut(auth);
  
      localStorage.clear();
  
      await router.replace(
        "/"
      );
  
    } catch (error) {
  
      console.error(
        "❌ Ошибка выхода:",
        error
      );
  
      errorMessage.value =
        "Не удалось выйти из аккаунта.";
  
    } finally {
  
      isLoading.value = false;
    }
  };
  </script>
  
  
  <style scoped>
  
  /* ============================= */
  /*          🧊 PAGE              */
  /* ============================= */
  
  .frozen-page {
    position: relative;
  
    width: 100%;
    min-height: 100dvh;
  
    display: flex;
    flex-direction: column;
  
    overflow: hidden;
  
    background:
      var(--bg, #f5f6f8);
  
    isolation: isolate;
  }
  
  
  /* ============================= */
  /*          ✨ GLOWS             */
  /* ============================= */
  
  .frozen-glow {
    position: absolute;
  
    border-radius: 999px;
  
    pointer-events: none;
  
    filter: blur(12px);
  
    z-index: -1;
  }
  
  
  .frozen-glow--one {
    width: 520px;
    height: 520px;
  
    top: -280px;
    left: 50%;
  
    transform: translateX(-70%);
  
    background:
      radial-gradient(
        circle,
        rgba(40, 135, 247, 0.20),
        rgba(40, 135, 247, 0)
        68%
      );
  }
  
  
  .frozen-glow--two {
    width: 420px;
    height: 420px;
  
    right: -180px;
    bottom: 40px;
  
    background:
      radial-gradient(
        circle,
        rgba(103, 199, 255, 0.12),
        rgba(103, 199, 255, 0)
        68%
      );
  }
  
  
  /* ============================= */
  /*          🧊 CONTENT           */
  /* ============================= */
  
  .frozen-content {
    flex: 1;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    width: 100%;
  
    padding:
      40px 20px 110px;
  
    box-sizing: border-box;
  }
  
  
  /* ============================= */
  /*            CARD              */
  /* ============================= */
  
  .frozen-card {
    width: min(
      100%,
      440px
    );
  
    padding:
      34px 32px 28px;
  
    box-sizing: border-box;
  
    border-radius: 26px;
  
    border:
      1px solid
      rgba(20, 30, 45, 0.07);
  
    background:
      rgba(255, 255, 255, 0.76);
  
    box-shadow:
      0 24px 70px
      rgba(26, 42, 65, 0.10);
  
    backdrop-filter:
      blur(28px);
  
    -webkit-backdrop-filter:
      blur(28px);
  
    text-align: center;
  }
  
  
  /* ============================= */
  /*          ❄️ ICON             */
  /* ============================= */
  
  .freeze-icon {
    display: flex;
    justify-content: center;
  
    margin-bottom: 22px;
  }
  
  
  .freeze-icon-inner {
    width: 62px;
    height: 62px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    border-radius: 20px;
  
    color: #2887f7;
  
    background:
      linear-gradient(
        145deg,
        rgba(40, 135, 247, 0.14),
        rgba(103, 199, 255, 0.07)
      );
  
    border:
      1px solid
      rgba(40, 135, 247, 0.10);
  
    box-shadow:
      0 10px 28px
      rgba(40, 135, 247, 0.10);
  }
  
  
  /* ============================= */
  /*            COPY              */
  /* ============================= */
  
  .frozen-copy {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  
  .frozen-badge {
    margin-bottom: 11px;
  
    padding:
      5px 10px;
  
    border-radius:
      999px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    letter-spacing:
      0.2px;
  
    color:
      #2479dc;
  
    background:
      rgba(40, 135, 247, 0.08);
  }
  
  
  .frozen-copy h1 {
    margin:
      0 0 10px;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 25px;
    line-height: 1.18;
  
    letter-spacing:
      -0.4px;
  
    color:
      #15171b;
  }
  
  
  .frozen-copy p {
    max-width: 350px;
  
    margin: 0;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 13px;
    line-height: 1.55;
  
    color:
      rgba(55, 62, 72, 0.64);
  }
  
  
  /* ============================= */
  /*            INFO              */
  /* ============================= */
  
  .frozen-info {
    display: flex;
    align-items: flex-start;
  
    gap: 9px;
  
    margin-top: 24px;
  
    padding:
      12px 14px;
  
    border-radius:
      13px;
  
    text-align: left;
  
    background:
      rgba(40, 135, 247, 0.055);
  }
  
  
  .info-dot {
    width: 6px;
    height: 6px;
  
    flex-shrink: 0;
  
    margin-top: 5px;
  
    border-radius:
      50%;
  
    background:
      #2887f7;
  
    box-shadow:
      0 0 0 4px
      rgba(40, 135, 247, 0.08);
  }
  
  
  .frozen-info span {
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 11px;
    line-height: 1.45;
  
    color:
      rgba(55, 65, 78, 0.67);
  }
  
  
  /* ============================= */
  /*           ACTIONS            */
  /* ============================= */
  
  .frozen-actions {
    display: flex;
    flex-direction: column;
  
    gap: 9px;
  
    margin-top: 22px;
  }
  
  
  .unfreeze-button,
  .logout-button {
    width: 100%;
    height: 46px;
  
    border: none;
  
    border-radius:
      14px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size:
      13px;
  
    cursor:
      pointer;
  
    transition:
      transform 0.18s ease,
      opacity 0.18s ease,
      background 0.18s ease,
      box-shadow 0.18s ease;
  }
  
  
  .unfreeze-button {
    color:
      #ffffff;
  
    background:
      linear-gradient(
        135deg,
        #2887f7,
        #176dd7
      );
  
    box-shadow:
      0 10px 24px
      rgba(40, 135, 247, 0.22);
  }
  
  
  .unfreeze-button:hover {
    transform:
      translateY(-1px);
  
    box-shadow:
      0 13px 30px
      rgba(40, 135, 247, 0.27);
  }
  
  
  .logout-button {
    color:
      rgba(40, 48, 60, 0.66);
  
    background:
      transparent;
  }
  
  
  .logout-button:hover {
    background:
      rgba(20, 30, 45, 0.045);
  }
  
  
  .unfreeze-button:disabled,
  .logout-button:disabled {
    opacity:
      0.56;
  
    cursor:
      default;
  
    transform:
      none;
  }
  
  
  /* ============================= */
  /*          ⏳ LOADING           */
  /* ============================= */
  
  .button-loading {
    display: flex;
    align-items: center;
    justify-content: center;
  
    gap: 8px;
  }
  
  
  .button-spinner {
    width: 13px;
    height: 13px;
  
    border-radius:
      50%;
  
    border:
      2px solid
      rgba(255, 255, 255, 0.38);
  
    border-top-color:
      #ffffff;
  
    animation:
      frozen-spin
      0.7s
      linear
      infinite;
  }
  
  
  @keyframes frozen-spin {
    to {
      transform:
        rotate(360deg);
    }
  }
  
  
  /* ============================= */
  /*           ❌ ERROR            */
  /* ============================= */
  
  .frozen-error {
    margin-top:
      14px;
  
    padding:
      10px 12px;
  
    border-radius:
      11px;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size:
      11px;
  
    line-height:
      1.4;
  
    color:
      #d84c4c;
  
    background:
      rgba(220, 60, 60, 0.07);
  }
  
  
  .error-fade-enter-active,
  .error-fade-leave-active {
    transition:
      opacity 0.2s ease;
  }
  
  
  .error-fade-enter-from,
  .error-fade-leave-to {
    opacity: 0;
  }
  
  
  /* ============================= */
  /*           FOOTER             */
  /* ============================= */
  
  .frozen-footer {
    position: relative;
    z-index: 2;
  
    width: 100%;
  }
  
  
  /* ============================= */
  /*       🌙 DARK THEME MODE      */
  /* ============================= */
  
  [data-theme="dark"] .frozen-page {
    background:
      #161616;
  }
  
  
  [data-theme="dark"] .frozen-card {
    border-color:
      rgba(255, 255, 255, 0.07);
  
    background:
      rgba(27, 27, 29, 0.78);
  
    box-shadow:
      0 26px 80px
      rgba(0, 0, 0, 0.27);
  }
  
  
  [data-theme="dark"]
  .freeze-icon-inner {
    color:
      #67b4ff;
  
    background:
      linear-gradient(
        145deg,
        rgba(40, 135, 247, 0.18),
        rgba(103, 199, 255, 0.07)
      );
  
    border-color:
      rgba(90, 160, 245, 0.13);
  }
  
  
  [data-theme="dark"]
  .frozen-badge {
    color:
      #78baff;
  
    background:
      rgba(40, 135, 247, 0.11);
  }
  
  
  [data-theme="dark"]
  .frozen-copy h1 {
    color:
      rgba(250, 250, 252, 0.96);
  }
  
  
  [data-theme="dark"]
  .frozen-copy p {
    color:
      rgba(225, 228, 235, 0.55);
  }
  
  
  [data-theme="dark"]
  .frozen-info {
    background:
      rgba(40, 135, 247, 0.08);
  }
  
  
  [data-theme="dark"]
  .frozen-info span {
    color:
      rgba(225, 230, 238, 0.55);
  }
  
  
  [data-theme="dark"]
  .logout-button {
    color:
      rgba(230, 233, 240, 0.57);
  }
  
  
  [data-theme="dark"]
  .logout-button:hover {
    background:
      rgba(255, 255, 255, 0.045);
  }
  
  
  /* ============================= */
  /*           📱 MOBILE           */
  /* ============================= */
  
  @media (max-width: 560px) {
  
    .frozen-content {
      align-items:
        center;
  
      padding:
        26px 15px
        calc(
          28px +
          env(safe-area-inset-bottom)
        );
    }
  
  
    .frozen-card {
      padding:
        28px 21px 22px;
  
      border-radius:
        22px;
    }
  
  
    .freeze-icon-inner {
      width:
        56px;
  
      height:
        56px;
  
      border-radius:
        18px;
    }
  
  
    .frozen-copy h1 {
      font-size:
        22px;
    }
  
  
    .frozen-copy p {
      font-size:
        12px;
    }
  
  
    .frozen-footer {
      display:
        none;
    }
  }
  </style>